import * as React from "react";
import {
  Button,
  Input,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ArrowClockwise24Regular, Search24Regular } from "@fluentui/react-icons";
import { DoctypeListItem } from "../../zegal/types";
import { listDoctypes, ApiError } from "../../zegal/api";
import { Settings } from "../../zegal/settings";

interface TemplateListProps {
  settings: Settings;
  onSelect: (item: DoctypeListItem) => void;
  onUnauthorized: () => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  searchRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
  },
  searchInput: { flex: 1 },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  card: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderRadius: "3px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    transitionProperty: "background-color",
    transitionDuration: "120ms",
    transitionTimingFunction: "ease",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    marginBottom: "2px",
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    padding: tokens.spacingVerticalL,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteRedBackground1,
  },
});

const TemplateList: React.FC<TemplateListProps> = ({ settings, onSelect, onUnauthorized }) => {
  const styles = useStyles();
  const [items, setItems] = React.useState<DoctypeListItem[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [submittedQuery, setSubmittedQuery] = React.useState("");

  const fetchTemplates = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listDoctypes(settings, { search: submittedQuery || undefined });
      setItems(response.result || []);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        onUnauthorized();
        return;
      }
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : (e as Error).message;
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [settings, submittedQuery, onUnauthorized]);

  React.useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return (
    <div className={styles.root}>
      <div className={styles.searchRow}>
        <Input
          className={styles.searchInput}
          contentBefore={<Search24Regular />}
          placeholder="Search templates"
          value={query}
          onChange={(_, d) => setQuery(d.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSubmittedQuery(query);
          }}
        />
        <Button
          icon={<ArrowClockwise24Regular />}
          aria-label="Refresh"
          onClick={() => {
            setSubmittedQuery(query);
            fetchTemplates();
          }}
        />
      </div>

      {loading && <Spinner labelPosition="after" label="Loading templates..." />}

      {error && <div className={styles.error}>Failed to load: {error}</div>}

      {!loading && !error && items && items.length === 0 && (
        <div className={styles.empty}>No templates found.</div>
      )}

      {!loading && items && items.length > 0 && (
        <div className={styles.list}>
          {items.map((item) => (
            <div
              key={item._id}
              className={styles.card}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(item)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSelect(item);
              }}
            >
              <div className={styles.title}>{item.title || "Untitled template"}</div>
              {item.jurisdictions && item.jurisdictions.length > 0 && (
                <div className={styles.meta}>{item.jurisdictions.join(", ")}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplateList;
